import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Input, InputProps } from "@chakra-ui/react";
import { useCombobox } from "downshift";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useDebouncedValue from "@/shared/hooks/useDebouncedValue";
import useGetPathPickAutocomplete from "@/features/pathpick/hooks/query/useGetPathPickAutocomplete";
import { PathPickAutocompleteSuggestion } from "@/features/pathpick/types/response";

interface Props {
  // 바깥(쿼리스트링 query)에서 정해진 검색어 — 바뀔 때만 입력창을 이 값으로 맞춘다.
  externalValue?: string;
  // 목록에 걸린 브랜드 필터 — SKU 제안을 이 브랜드들로 좁힌다.
  brandIds?: number[];
  // 제안 범위 — 주면 브랜드 제안은 이 브랜드만 남기고, 브랜드 필터가 없을 때 SKU 제안도 이 브랜드로 좁힌다.
  // 자동완성 API는 패스픽 전체 기준이라, 담은 상품처럼 목록이 좁은 화면에서 고르면 0건이 될 제안을 거른다.
  // 빈 배열이면 범위 안에 브랜드가 없는 것이라 묻지 않는다. 생략하면 전체 기준 그대로다.
  brandScope?: number[];
  // 입력창 글자가 바뀔 때마다(타이핑·선택·지우기) 현재 글자를 알린다 — 부모는 검색 버튼에 쓴다.
  onChange: (value: string) => void;
  // Enter — 자동완성 항목을 고르지 않은 채 친 검색어로 검색한다.
  onSubmit: () => void;
  onSelectBrand: (brandId: number, brandName: string) => void;
  onSelectSku: (sku: string) => void;
  placeholder?: string;
  width?: string;
  inputProps?: InputProps;
}

// 키 입력이 멈춘 뒤 이만큼 지나야 자동완성을 묻는다 — 글자마다 요청이 나가지 않게.
const AUTOCOMPLETE_DEBOUNCE_MS = 250;

// 패스픽 검색창(상품 찾기·담은 상품) — 입력에 맞춰 브랜드·단품오더 SKU 를 제안한다.
// 모양은 오더하기의 자동완성(features/ui/app-autocomplete-input)과 같다: 테두리 상자 + 검색 아이콘 + x 지우기,
// 목록은 회색 종류(Brand/SKU) 위에 라벨.
//
// 입력 글자는 downshift 가 가진다(비제어). 부모 state 로 제어하면 downshift 가 내부 상태와 제어값의 차이를
// 보고 지난 입력 콜백을 다시 쏴서 글자가 되돌아가는 문제가 있었다(2026-10-07 로컬 재현).
// 부모는 onChange 로 글자를 받기만 하고, 바깥에서 정한 검색어(externalValue)가 바뀔 때만 setInputValue 로 맞춘다.
export default function PathPickAutocompleteInput({
  externalValue,
  brandIds,
  brandScope,
  onChange,
  onSubmit,
  onSelectBrand,
  onSelectSku,
  placeholder = "",
  width = "100%",
  inputProps,
}: Props) {
  const { t } = useI18nHelper();
  // 입력 글자의 사본 — 제안 조회(디바운스)와 목록 표시에만 쓴다. 입력창을 제어하지는 않는다.
  const [text, setText] = useState(externalValue ?? "");
  const debouncedQuery = useDebouncedValue(
    text.trim(),
    AUTOCOMPLETE_DEBOUNCE_MS
  );
  const isScopeEmpty = brandScope !== undefined && brandScope.length === 0;
  const { suggestions } = useGetPathPickAutocomplete(
    isScopeEmpty ? "" : debouncedQuery,
    brandIds?.length ? brandIds : brandScope
  );
  // 브랜드 제안은 서버가 범위를 모르므로 여기서 거른다(SKU 는 위에서 넘긴 브랜드로 서버가 좁혔다).
  const scopedSuggestions = useMemo(() => {
    if (!brandScope) {
      return suggestions;
    }
    const scope = new Set(brandScope);
    return suggestions.filter(
      (item) => item.type !== "Brand" || scope.has(Number(item.value))
    );
  }, [suggestions, brandScope]);
  // 입력을 지우면 이전 검색어의 제안이 남아 있어도 보이지 않게 한다.
  const items = text.trim() ? scopedSuggestions : [];

  const {
    isOpen,
    inputValue,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    setInputValue,
  } = useCombobox<PathPickAutocompleteSuggestion>({
    items,
    defaultInputValue: externalValue ?? "",
    // 항상 비워 두어 같은 항목을 연달아 골라도 매번 onSelectedItemChange 가 불리게 한다.
    selectedItem: null,
    itemToString: (item) => (item ? item.label : ""),
    stateReducer: (state, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const item = changes.selectedItem;
          if (!item) {
            return changes;
          }
          // 브랜드는 필터로 가므로 입력을 비우고, SKU 는 원본 SKU 를 입력에 남긴다.
          return {
            ...changes,
            inputValue: item.type === "Brand" ? "" : item.value,
          };
        }
        case useCombobox.stateChangeTypes.InputBlur:
          // 포커스가 나가도 친 글자는 남긴다 — 검색 버튼으로 이어서 검색할 수 있게.
          return { ...changes, inputValue: state.inputValue };
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          // selectedItem 을 null 로 제어하면 downshift 가 항목을 고른 다음 렌더에서 "제어값이 null 로
          // 바뀌었다"고 보고 입력을 비운다 — SKU 를 골라 넣은 글자가 사라지므로 막는다.
          return { ...changes, inputValue: state.inputValue };
        default:
          return changes;
      }
    },
    onInputValueChange: ({ inputValue: next }) => {
      setText(next ?? "");
      onChange(next ?? "");
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }
      if (selectedItem.type === "Brand") {
        onSelectBrand(Number(selectedItem.value), selectedItem.label);
      } else {
        onSelectSku(selectedItem.value);
      }
    },
  });

  // 바깥에서 검색어가 바뀌면(초기화·전체 해제·SKU 선택 후 조회) 입력창을 그 값으로 맞춘다.
  useEffect(() => {
    setInputValue(externalValue ?? "");
  }, [externalValue, setInputValue]);

  const showMenu = isOpen && items.length > 0;

  return (
    <Flex
      fontSize={"12px"}
      flexDirection={"row"}
      gap={"0px"}
      flexShrink={0}
      width={width}
      border={"1px solid #CBD5E0"}
      borderRadius={"4px"}
      _focusWithin={{
        border: "1px solid #3182ce",
        boxShadow: "0 0 0 1px #3182ce",
      }}
    >
      <Box position={"relative"} width="100%">
        <SVG
          src={`/icons-v1_1/search.svg`}
          width={"20px"}
          height={"20px"}
          style={{
            position: "absolute",
            top: "50%",
            left: "6px",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        />
        <Input
          {...getInputProps({
            placeholder,
            onKeyDown: (event) => {
              // 강조된 항목이 없을 때의 Enter 는 검색이다. 있으면 downshift 가 그 항목을 고른다.
              if (
                event.key === "Enter" &&
                (!showMenu || highlightedIndex < 0)
              ) {
                event.preventDefault();
                (
                  event.nativeEvent as Event & {
                    preventDownshiftDefault?: boolean;
                  }
                ).preventDownshiftDefault = true;
                onSubmit();
              }
            },
          })}
          padding={"0 8px"}
          margin={"0"}
          minHeight={"28px"}
          height={"28px"}
          fontSize={"inherit"}
          textAlign={"start"}
          paddingLeft={"26px"}
          paddingRight="26px"
          border={"none"}
          _focus={{ boxShadow: "none", border: "none" }}
          _hover={{ border: "none" }}
          {...inputProps}
        />
        {inputValue.length > 0 && (
          <Box
            width="20px"
            height="20px"
            cursor="pointer"
            position="absolute"
            top="50%"
            right="6px"
            transform="translateY(-50%)"
            zIndex={10}
            onClick={() => setInputValue("")}
            aria-label="clear"
          >
            <SVG
              src="/icons-v1_1/x-circle-solid.svg"
              width={"20px"}
              height={"20px"}
            />
          </Box>
        )}
        {/* getMenuProps 는 항상 붙여야 한다(안 붙이면 downshift 가 경고한다) — 닫혀 있을 땐 테두리 없이 비워 둔다. */}
        <Box
          {...getMenuProps()}
          position="absolute"
          zIndex={10}
          bg="white"
          width="100%"
          maxHeight="200px"
          overflowY="auto"
          border={showMenu ? "1px solid #ccc" : ""}
          borderRadius="md"
          mt={1}
        >
          {showMenu &&
            items.map((item, index) => (
              <Box
                key={`${item.type}-${item.value}-${index}`}
                {...getItemProps({ item, index })}
                bg={highlightedIndex === index ? "gray.100" : "white"}
                px={3}
                py={2}
                cursor="pointer"
              >
                <Box>
                  <strong style={{ color: "#999" }}>{item.type}</strong>
                </Box>{" "}
                {item.label}
                {item.type === "SKU" && item.inStock === false && (
                  <span style={{ color: "#999", marginLeft: "6px" }}>
                    {t("path_pick:autocomplete.sold_out")}
                  </span>
                )}
              </Box>
            ))}
        </Box>
      </Box>
    </Flex>
  );
}
